import React from 'react';
import Typography from '../Text/Typography';
import Colors from '../Colors/Colors';
import Icon, { IconName } from '../Icon/Icon';
import './Banner.css';

export type BannerVariant = 'caution' | 'warning' | 'info' | 'tip' | 'expandable-info';

interface BaseBannerProps {
  variant: BannerVariant;
  title?: string;
  children: React.ReactNode;
  className?: string;
}

interface ExpandableInfoBannerProps {
  variant: 'expandable-info';
  title: string;
  summary: string;
  content: React.ReactNode;
  defaultExpanded?: boolean;
  persistent?: boolean;
  className?: string;
}

type BannerProps = BaseBannerProps | ExpandableInfoBannerProps;

function isExpandableInfoProps(props: BannerProps): props is ExpandableInfoBannerProps {
  return props.variant === 'expandable-info';
}

const Banner: React.FC<BannerProps> = (props) => {
  const { variant, className = '' } = props;

  // Expandable-info variant: collapsible banner with title, summary, and expandable content
  if (isExpandableInfoProps(props)) {
    const {
      title,
      summary,
      content,
      defaultExpanded = false,
      persistent = true,
    } = props;
    const [expanded, setExpanded] = React.useState(defaultExpanded);
    const toggleId = React.useId();
    const contentId = `banner-expandable-content-${toggleId.replace(/:/g, '')}`;

    return (
      <div className={`banner banner-info banner-expandable-info ${className}`} role="region" aria-label={title}>
        <div className="banner-content">
          <div className="banner-icon">
            <Colors variant="black">
              <Icon name="sparkles" size="large" />
            </Colors>
          </div>
          <div className="banner-text banner-expandable-text">
            <Colors variant="black">
              <div className="banner-expandable-header">
                <div className="banner-expandable-summary">
                  <Typography variant="body-small-bold" className="banner-title">
                    {title}
                  </Typography>
                  {summary ? (
                    <Typography variant="body-small" className="banner-expandable-summary-line">
                      {summary}
                    </Typography>
                  ) : null}
                </div>
                <button
                  type="button"
                  className="banner-expandable-toggle"
                  onClick={() => setExpanded((e) => !e)}
                  aria-expanded={expanded}
                  aria-controls={contentId}
                  id={`${contentId}-toggle`}
                  aria-label={expanded ? 'Collapse details' : 'Expand details'}
                >
                  <Icon
                    name={expanded ? 'chevron-up' : 'chevron-down'}
                    size="large"
                    aria-hidden
                  />
                </button>
              </div>
              <div
                id={contentId}
                role="region"
                aria-labelledby={`${contentId}-toggle`}
                className={`banner-expandable-content ${expanded ? 'banner-expandable-content-open' : ''}`}
              >
                <div className="banner-expandable-content-inner">
                  {content}
                </div>
              </div>
            </Colors>
          </div>
        </div>
      </div>
    );
  }

  // Standard variants
  const { title, children } = props;
  const getVariantConfig = () => {
    switch (variant) {
      case 'caution':
        return {
          icon: 'alert' as IconName,
          backgroundVariant: 'pastelle-yellow' as const,
          textVariant: 'black' as const,
          title: title || 'Caution'
        };
      case 'warning':
        return {
          icon: 'alert' as IconName,
          backgroundVariant: 'pastelle-red' as const,
          textVariant: 'black' as const,
          title: title || 'Warning'
        };
      case 'info':
        return {
          icon: 'info' as IconName,
          backgroundVariant: 'pastelle-blue' as const,
          textVariant: 'black' as const,
          title: title || 'Information'
        };
      case 'tip':
        return {
          icon: 'idea' as IconName,
          backgroundVariant: 'pastelle-yellow' as const,
          textVariant: 'black' as const,
          title: title || 'Tip'
        };
      case 'expandable-info':
        return {
          icon: 'info' as IconName,
          backgroundVariant: 'pastelle-blue' as const,
          textVariant: 'black' as const,
          title: title || 'Information'
        };
      default:
        return {
          icon: 'info' as IconName,
          backgroundVariant: 'pastelle-blue' as const,
          textVariant: 'black' as const,
          title: title || 'Information'
        };
    }
  };

  const config = getVariantConfig();

  return (
    <div className={`banner banner-${variant} ${className}`}>
      <div className="banner-content">
        <div className="banner-icon">
          <Colors variant={config.textVariant}>
            <Icon name={config.icon} size="large" />
          </Colors>
        </div>
        <div className="banner-text">
          <Colors variant={config.textVariant}>
            <Typography variant="body-small-bold" className="banner-title">
              {config.title}
            </Typography>
            <div className="banner-message typography typography-body-small">
              {children}
            </div>
          </Colors>
        </div>
      </div>
    </div>
  );
};

export default Banner; 